import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import JsBarcode from "jsbarcode";
import { LOGO_URL, DOCUMENT_TYPES } from "./constants";

// Linear (CODE128) barcode encoding the ACK number — synchronous, rendered
// via an offscreen canvas, unlike the QR generation which is async.
const generateBarcodeDataUrl = (value) => {
  if (!value) return null;
  try {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, String(value), { format: "CODE128", displayValue: false, margin: 0, height: 40 });
    return canvas.toDataURL("image/png");
  } catch (e) {
    return null;
  }
};

const INK = [17, 17, 17];
const SUB = [70, 70, 70];
const MUTED = [130, 130, 130];
const HAIR = [200, 200, 200];
const SOFT = [242, 242, 242];
const BRAND = [182, 15, 42];
const BRAND_SOFT = [251, 231, 234];

const loadImageDataUrl = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(null);
    if (url.startsWith("data:")) return resolve(url);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

const fmtDate = (iso) => {
  if (!iso) return "________________";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
};

const truncateFactory = (doc) => (value, maxW) => {
  const s = String(value);
  if (doc.getTextWidth(s) <= maxW) return s;
  let lo = 0;
  let hi = s.length;
  while (lo < hi) {
    const mid = Math.floor((lo + hi + 1) / 2);
    if (doc.getTextWidth(s.slice(0, mid) + "…") <= maxW) lo = mid;
    else hi = mid - 1;
  }
  return s.slice(0, lo) + "…";
};

// ---- shared helpers ----
const drawCopyStamp = (doc, label) => {
  const x = 30;
  const y = 24;
  const angle = 12;
  const tw = doc.getTextWidth(label);
  const pad = 3;
  doc.saveGraphicsState();
  const rad = (angle * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  doc.internal.write(`q ${cos.toFixed(4)} ${sin.toFixed(4)} ${(-sin).toFixed(4)} ${cos.toFixed(4)} ${x.toFixed(4)} ${y.toFixed(4)} cm`);
  doc.setLineWidth(0.6);
  doc.setDrawColor(...INK);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(-tw / 2 - pad, -3.5, tw + pad * 2, 7, 1, 1, "FD");
  doc.internal.write("Q");
  doc.restoreGraphicsState();
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.text(label, x, y + 1.3, { align: "center", angle: -angle });
};

const drawHeader = (doc, logoData, copyLabel) => {
  const pageW = doc.internal.pageSize.getWidth();
  if (logoData) doc.addImage(logoData, "PNG", pageW - 12 - 42, 9, 42, 13);
  if (copyLabel) drawCopyStamp(doc, copyLabel);
};

const drawFooter = (doc, pageIdx, totalPages, ackNumber) => {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const barcodeData = generateBarcodeDataUrl(ackNumber);
  if (barcodeData) {
    const bw = 32;
    const bh = 9;
    doc.addImage(barcodeData, "PNG", 12, pageH - 12 - bh - 2, bw, bh);
  }
  doc.setDrawColor(...HAIR);
  doc.setLineWidth(0.2);
  doc.line(12, pageH - 12, pageW - 12, pageH - 12);
  doc.setTextColor(...MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(`ACK: ${ackNumber || "—"}`, 12, pageH - 6);
  doc.text("Mahindra University", pageW / 2, pageH - 6, { align: "center" });
  doc.text(`Page ${pageIdx} of ${totalPages}`, pageW - 12, pageH - 6, { align: "right" });
};

const drawTitleBlock = (doc, y, schoolName) => {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("MAHINDRA UNIVERSITY", pageW / 2, y, { align: "center" });
  doc.setFont("helvetica", "normal");
  const subtitle = `${(schoolName || "SCHOOL OF MANAGEMENT").toUpperCase()} - UNDER GRADUATE`;
  // Centered on the full page width, so it has to stay clear of the QR code
  // reserved in the top-right corner — school names are no longer a fixed
  // short string (multiple schools now), so shrink to fit instead of
  // overlapping when a longer one (e.g. "Ecole Centrale School of
  // Engineering") doesn't fit at the default size.
  let subtitleSize = 11;
  doc.setFontSize(subtitleSize);
  const maxSubtitleWidth = pageW - 116;
  while (doc.getTextWidth(subtitle) > maxSubtitleWidth && subtitleSize > 7) {
    subtitleSize -= 0.5;
    doc.setFontSize(subtitleSize);
  }
  doc.text(subtitle, pageW / 2, y + 8, { align: "center" });
  doc.setFontSize(11);
  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.text("REGISTRATION & ADMISSIONS", pageW / 2, y + 18, { align: "center" });
  doc.setFontSize(11);
  doc.text("ADMISSION YEAR - 2026", pageW / 2, y + 28, { align: "center" });
  return y + 38;
};

const drawUnderlinedHeading = (doc, y, text, fontSize = 12) => {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fontSize);
  const tw = doc.getTextWidth(text);
  const x = pageW / 2 - tw / 2;
  doc.text(text, pageW / 2, y, { align: "center" });
  doc.setLineWidth(0.4);
  doc.setDrawColor(...INK);
  doc.line(x, y + 1.2, x + tw, y + 1.2);
  return y + fontSize * 0.5 + 4;
};

// =========================================================
//                CANDIDATE PDF (single page)
// =========================================================
const drawCandidateSummaryPage = (doc, state, logoData, qrData, photoData) => {
  const pageW = doc.internal.pageSize.getWidth();
  drawHeader(doc, logoData, null);

  if (qrData) {
    const qrSize = 46;
    const qrY = 25;
    doc.addImage(qrData, "PNG", pageW - 12 - qrSize, qrY, qrSize, qrSize);
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text("Scan to verify", pageW - 12 - qrSize / 2, qrY + qrSize + 5, { align: "center" });
  }

  let y = 30;
  y = drawTitleBlock(doc, y, state.courseInfo?.school);
  y = drawUnderlinedHeading(doc, y + 8, "REGISTRATION SUMMARY", 12);
  const truncate = truncateFactory(doc);

  const boxX = 18;
  const boxW = pageW - 36;
  const boxH = 58;
  y += 3;

  doc.setDrawColor(...INK);
  doc.setLineWidth(0.3);
  doc.roundedRect(boxX, y, boxW, boxH, 1.5, 1.5, "S");

  const photoW = 26, photoH = 33;
  const photoX = boxX + 4, photoY = y + 4;
  doc.setFillColor(...SOFT);
  doc.rect(photoX, photoY, photoW, photoH, "F");
  doc.setDrawColor(...HAIR);
  doc.rect(photoX, photoY, photoW, photoH, "S");
  if (photoData) {
    try { doc.addImage(photoData, "PNG", photoX + 1, photoY + 1, photoW - 2, photoH - 2); } catch (_e) { /* photo add failed */ }
  } else {
    doc.setTextColor(...MUTED); doc.setFontSize(8);
    doc.text("No Photo", photoX + photoW / 2, photoY + photoH / 2, { align: "center" });
  }

  const c = state.candidate || {};
  const rollNumber = state.assignedSlot?.rollNumber || "—";
  const rows = [
    ["Application No.", state.applicationNumber || "—"],
    ["ACK Number", state.ackNumber || "—"],
    ["Candidate Name", c.name || "—"],
    ["Mobile", state.mobile || "—"],
    ["Nationality", c.nationality || "—"],
    ["Anti-Ragging Ref.", c.affidavitRef || "—"],
    ["Submitted On", state.submittedAt ? new Date(state.submittedAt).toLocaleString() : "—"],
  ];
  const detailsX = photoX + photoW + 6;
  const valueX = detailsX + 34;
  const valueMaxW = boxX + boxW - 4 - valueX;
  let dy = y + 7;
  const rowH = 6.7;
  rows.forEach(([k, v]) => {
    doc.setTextColor(...MUTED); doc.setFont("helvetica", "normal"); doc.setFontSize(7.4);
    doc.text(k.toUpperCase(), detailsX, dy);
    doc.setTextColor(...INK); doc.setFont("helvetica", "bold"); doc.setFontSize(9.5);
    doc.text(truncate(v, valueMaxW), valueX, dy);
    dy += rowH;
  });

  y += boxH + 6;

  // Roll Number banner — beautified: soft brand-tinted card with a bold
  // brand-red accent instead of a solid black fill, centered for emphasis.
  const bannerH = 27;
  doc.setFillColor(...BRAND_SOFT);
  doc.roundedRect(boxX, y, boxW, bannerH, 2, 2, "F");
  doc.setDrawColor(...BRAND);
  doc.setLineWidth(0.5);
  doc.roundedRect(boxX, y, boxW, bannerH, 2, 2, "S");
  doc.setTextColor(...SUB);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("YOUR ALLOTTED ROLL NUMBER", boxX + boxW / 2, y + 9, { align: "center" });
  doc.setTextColor(...BRAND);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.text(rollNumber, boxX + boxW / 2, y + 21, { align: "center" });
  y += bannerH + 6;

  // Reporting details — VENUE gets its own full-width row so long venue
  // names never get truncated; DATE / TIME / SEAT sit on the row below.
  y = drawUnderlinedHeading(doc, y, "REPORTING DETAILS", 12);
  y += 3;
  const slot = state.assignedSlot;
  const venueRowH = 14;
  const restRowH = 20;
  const stripH = venueRowH + restRowH;
  doc.setDrawColor(...INK);
  doc.setLineWidth(0.3);
  doc.roundedRect(boxX, y, boxW, stripH, 1.5, 1.5, "S");

  doc.setTextColor(...MUTED); doc.setFont("helvetica", "normal"); doc.setFontSize(7);
  doc.text("VENUE", boxX + 4, y + 5.5);
  doc.setTextColor(...INK); doc.setFont("helvetica", "bold"); doc.setFontSize(10);
  doc.text(truncate(String(slot?.venue || "School of Management Building"), boxW - 8), boxX + 4, y + 11.5);
  doc.setDrawColor(...HAIR); doc.setLineWidth(0.2);
  doc.line(boxX, y + venueRowH, boxX + boxW, y + venueRowH);

  const row2Y = y + venueRowH;
  const fields = [
    ["DATE", slot?.dateLabel || "—"],
    ["TIME", slot?.timeLabel || "—"],
    ["SEAT / SLOT", slot ? (slot.mode === "token" || slot.mode === "walkin" ? "Token at check-in" : `Seat #${slot.seatInSlot} · Slot ${slot.slotIndex}`) : "—"],
  ];
  const colW = boxW / 3;
  fields.forEach(([k, v], i) => {
    const cx = boxX + i * colW + 4;
    const maxW = colW - 6;
    doc.setTextColor(...MUTED); doc.setFont("helvetica", "normal"); doc.setFontSize(7);
    doc.text(k, cx, row2Y + 7);
    doc.setTextColor(...INK); doc.setFont("helvetica", "bold"); doc.setFontSize(9.5);
    doc.text(truncate(String(v), maxW), cx, row2Y + 14);
    if (i > 0) {
      doc.setDrawColor(...HAIR); doc.setLineWidth(0.2);
      doc.line(boxX + i * colW, row2Y + 3, boxX + i * colW, row2Y + restRowH - 3);
    }
  });
  y += stripH + 6;

  const noteH = 30;
  doc.setDrawColor(...INK); doc.setLineWidth(0.3);
  doc.roundedRect(boxX, y, boxW, noteH, 1.5, 1.5, "S");
  doc.setTextColor(...INK); doc.setFont("helvetica", "bold"); doc.setFontSize(9.5);
  doc.text("Please note", boxX + 4, y + 6);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  const noteLines = doc.splitTextToSize(
    "Please take a hard copy (print out) of this Registration Summary Slip and carry it along with all original documents on your reporting day. Please also carry 2 passport-size photographs for verification. Reporting slots are allotted on a first-come, first-serve basis.",
    boxW - 8
  );
  let ny = y + 12;
  noteLines.forEach((l) => { doc.text(l, boxX + 4, ny); ny += 4.5; });
};

export const downloadCandidatePdf = async (state) => {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const qrPayload = JSON.stringify({ ack_number: state.ackNumber || "" });
  const [logoData, qrData, photoData] = await Promise.all([
    loadImageDataUrl(LOGO_URL),
    QRCode.toDataURL(qrPayload, { margin: 1, width: 360, color: { dark: "#111111", light: "#FFFFFF" } }).catch(() => null),
    loadImageDataUrl(state.candidate?.photo?.file_url || state.candidate?.photo?.dataUrl),
  ]);
  drawCandidateSummaryPage(doc, state, logoData, qrData, photoData);
  drawFooter(doc, 1, 1, state.ackNumber);
  doc.save(`MU-Registration-Summary-${state.ackNumber || "ACK"}.pdf`);
};

// =========================================================
//                ADMIN ACK PDF (Student + Office)
// =========================================================
const drawAckPage = (doc, app, logoData, qrData, copyLabel) => {
  const pageW = doc.internal.pageSize.getWidth();
  drawHeader(doc, logoData, copyLabel);

  if (qrData) {
    doc.addImage(qrData, "PNG", pageW - 12 - 18, 22, 18, 18);
    doc.setTextColor(...MUTED); doc.setFont("helvetica", "normal"); doc.setFontSize(6.5);
    doc.text("Scan to verify", pageW - 12 - 9, 43, { align: "center" });
  }

  let y = 30;
  y = drawTitleBlock(doc, y, app.course_info?.school || app.courseInfo?.school);
  y = drawUnderlinedHeading(doc, y + 2, "ACKNOWLEDGEMENT", 13);

  // Paragraph with roll number
  const marginX = 18;
  const maxW = pageW - marginX * 2;
  const name = app.name || app.candidateName || "________________________";
  const appNo = app.applicationNumber || "________________";
  const ackNo = app.ackNumber || "________________";
  const rollNumber = app.rollNumber || app.assignedSlot?.rollNumber || "________";
  const dateStr = fmtDate(app.submittedAt);
  const sentence = `This is to acknowledge that Mahindra University has received below certificates in ORIGINALS from ${name} (Application No: ${appNo} / ${ackNo}, Roll No: ${rollNumber}) on ${dateStr}.`;
  doc.setTextColor(...INK); doc.setFont("helvetica", "normal"); doc.setFontSize(10.5);
  const lines = doc.splitTextToSize(sentence, maxW);
  let cy = y + 2;
  lines.forEach((line) => { doc.text(line, marginX, cy); cy += 5.2; });
  y = cy + 3;

  y = drawUnderlinedHeading(doc, y, "TO BE FILLED BY THE VERIFYING OFFICER ONLY", 11);

  // Documents table — 2 columns only
  const originalReceived = app.originalReceived || {};
  const body = DOCUMENT_TYPES.map((d, i) => {
    const files = app.documents?.[d.id] || [];
    const present = files.length > 0;
    // ticked if admin marked originalReceived[d.id]===true
    return [String(i + 1), d.label + (present ? "" : ""), ""];
  });

  autoTable(doc, {
    startY: y + 2,
    head: [["#", "Nature of Document", "In Original"]],
    body,
    theme: "grid",
    styles: { font: "helvetica", fontSize: 9.5, cellPadding: 2.5, textColor: INK, lineColor: INK, lineWidth: 0.25, overflow: "linebreak", minCellHeight: 8 },
    headStyles: { fillColor: SOFT, textColor: INK, fontStyle: "bold", fontSize: 10, halign: "center", lineColor: INK, lineWidth: 0.3 },
    columnStyles: {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: maxW - 10 - 40, halign: "left" },
      2: { cellWidth: 40, halign: "center" },
    },
    didDrawCell: (data) => {
      if (data.section === "body" && data.column.index === 2) {
        const cx = data.cell.x + data.cell.width / 2 - 2.5;
        const cy2 = data.cell.y + data.cell.height / 2 - 2.5;
        doc.setDrawColor(...INK); doc.setLineWidth(0.3);
        doc.rect(cx, cy2, 5, 5);
        // Tick if admin marked originalReceived
        const docType = DOCUMENT_TYPES[data.row.index];
        if (docType && originalReceived[docType.id]) {
          doc.setDrawColor(...INK); doc.setLineWidth(0.6);
          // Draw a tick mark
          doc.line(cx + 0.6, cy2 + 2.6, cx + 2.2, cy2 + 4.2);
          doc.line(cx + 2.2, cy2 + 4.2, cx + 4.6, cy2 + 0.6);
        }
      }
    },
    margin: { left: marginX, right: marginX },
  });

  let afterY = doc.lastAutoTable.finalY + 6;

  // "To be signed in the presence of verifying officer only"
  doc.setTextColor(...INK); doc.setFont("helvetica", "bolditalic"); doc.setFontSize(10);
  doc.text("(To be signed in the presence of verifying officer only)", pageW / 2, afterY, { align: "center" });
  afterY += 6;

  // Declaration
  const decl =
    'I hereby confirm that I have submitted the documents tick-marked under the "In Original" column only. I understand and acknowledge that all my original documents, except the Transfer Certificate (TC), may be collected at any time after 31 October 2026.';
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  const declLines = doc.splitTextToSize(decl, maxW);
  declLines.forEach((line) => { doc.text(line, marginX, afterY); afterY += 5; });

  afterY += 6;
  // Signature + Date (no "Student Name" label)
  doc.setFont("helvetica", "bold"); doc.setFontSize(10);
  const lineW1 = 80;
  doc.text("Signature", marginX, afterY);
  doc.setDrawColor(...INK); doc.setLineWidth(0.25);
  doc.line(marginX + 22, afterY + 1, marginX + 22 + lineW1, afterY + 1);

  const dateX = marginX + 22 + lineW1 + 15;
  doc.text("Date", dateX, afterY);
  doc.setFont("helvetica", "normal");
  doc.text("13/07/2026", dateX + 15, afterY);
  doc.line(dateX + 12, afterY + 1, dateX + 55, afterY + 1);
};

export const downloadAdminAckPdf = async (app) => {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const qrPayload = JSON.stringify({ ack_number: app.ackNumber || "" });
  const [logoData, qrData] = await Promise.all([
    loadImageDataUrl(LOGO_URL),
    QRCode.toDataURL(qrPayload, { margin: 1, width: 260, color: { dark: "#111111", light: "#FFFFFF" } }).catch(() => null),
  ]);
  drawAckPage(doc, app, logoData, qrData, "STUDENT COPY");
  doc.addPage();
  drawAckPage(doc, app, logoData, qrData, "OFFICE COPY");
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc, i, totalPages, app.ackNumber);
  }
  doc.save(`MU-Acknowledgement-${app.ackNumber || "ACK"}.pdf`);
};

// Open the PDF in a new tab and immediately trigger the browser print dialog.
// Users then print directly — no download-then-open detour.
export const printAdminAckPdf = async (app) => {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const qrPayload = JSON.stringify({ ack_number: app.ackNumber || "" });
  const [logoData, qrData] = await Promise.all([
    loadImageDataUrl(LOGO_URL),
    QRCode.toDataURL(qrPayload, { margin: 1, width: 260, color: { dark: "#111111", light: "#FFFFFF" } }).catch(() => null),
  ]);
  drawAckPage(doc, app, logoData, qrData, "STUDENT COPY");
  doc.addPage();
  drawAckPage(doc, app, logoData, qrData, "OFFICE COPY");
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc, i, totalPages, app.ackNumber);
  }
  doc.autoPrint();
  const url = doc.output("bloburl");
  const w = window.open(url, "_blank");
  if (!w) {
    // Fallback if pop-ups are blocked: use hidden iframe
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.src = url;
    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch (e) {
        // ignore
      }
    };
    document.body.appendChild(iframe);
  }
};

// Back-compat alias — Success page uses this
export const downloadAckPdf = downloadCandidatePdf;

// =========================================================
//         ADMIN OVERVIEW — Registrations Summary PDF
// =========================================================

const OVERVIEW_STAT_KEYS = [
  ["total_offer_letters", "Offer Letters"],
  ["not_started", "Not Started"],
  ["in_progress", "In Progress"],
  ["submitted", "Submitted"],
  ["approved", "Approved"],
  ["withdrawn", "Withdrawn"],
  ["withdrawn_admission_level", "Withdrawn (Admission)"],
  ["withdrawn_course_level", "Withdrawn (Course)"],
];

const _emptyOverviewTotals = () =>
  Object.fromEntries(OVERVIEW_STAT_KEYS.map(([k]) => [k, 0]));

const _collapseProgramStats = (cohortStats) => {
  const groups = new Map();
  for (const c of cohortStats || []) {
    const label = (c.label || "").replace(/\s*\([^)]*\)/, "");
    const existing = groups.get(label);
    if (existing) {
      OVERVIEW_STAT_KEYS.forEach(([k]) => {
        existing[k] += c[k] || 0;
      });
    } else {
      const row = { label };
      OVERVIEW_STAT_KEYS.forEach(([k]) => {
        row[k] = c[k] || 0;
      });
      groups.set(label, row);
    }
  }
  return Array.from(groups.values());
};

const _sumOverviewStats = (rows) =>
  (rows || []).reduce((acc, c) => {
    OVERVIEW_STAT_KEYS.forEach(([k]) => {
      acc[k] += c[k] || 0;
    });
    return acc;
  }, _emptyOverviewTotals());

const _drawOverviewHeader = (doc, logoData, reportDate) => {
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 14;

  if (logoData) {
    try {
      const props = doc.getImageProperties(logoData);
      const maxW = 42;
      const maxH = 18;
      let w = maxW;
      let h = (props.height / props.width) * w;
      if (h > maxH) {
        h = maxH;
        w = (props.width / props.height) * h;
      }
      doc.addImage(logoData, "PNG", margin, 12, w, h);
    } catch (_e) { /* ignore */ }
  }

  doc.setTextColor(...BRAND);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Registrations Summary", pageW / 2, 20, { align: "center" });

  doc.setTextColor(...MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Date: ${reportDate}`, pageW / 2, 27, { align: "center" });

  doc.setDrawColor(...BRAND);
  doc.setLineWidth(0.6);
  doc.line(margin, 32, pageW - margin, 32);

  return 38;
};

const _overviewTableStyles = {
  headStyles: {
    fillColor: BRAND,
    textColor: [255, 255, 255],
    fontStyle: "bold",
    fontSize: 7.5,
    halign: "center",
  },
  bodyStyles: {
    fontSize: 7.5,
    textColor: INK,
    halign: "center",
  },
  alternateRowStyles: { fillColor: SOFT },
  styles: {
    lineColor: HAIR,
    lineWidth: 0.2,
    cellPadding: 1.6,
    overflow: "linebreak",
  },
  margin: { left: 14, right: 14 },
};

/**
 * Download a branded PDF of Overview stats across all provided cohort rows.
 * Includes overall summary, program-collapsed table, and per-cohort table.
 */
export const downloadRegistrationsSummaryPdf = async (cohortStats) => {
  const logoData = await loadImageDataUrl(LOGO_URL);
  const programs = _collapseProgramStats(cohortStats);
  const overall = _sumOverviewStats(cohortStats);
  const reportDate = new Date().toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
  const generatedAt = new Date().toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  });

  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
  const pageW = doc.internal.pageSize.getWidth();
  let y = _drawOverviewHeader(doc, logoData, reportDate);

  doc.setTextColor(...MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(
    `${(cohortStats || []).length} cohort(s) · ${programs.length} program(s) · Generated ${generatedAt}`,
    14,
    y
  );
  y += 6;

  // ---- Overall Summary ----
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Overall Summary", 14, y);
  y += 2;

  autoTable(doc, {
    ..._overviewTableStyles,
    startY: y,
    head: [["Metric", "Count"]],
    body: OVERVIEW_STAT_KEYS.map(([key, label]) => [label, String(overall[key] ?? 0)]),
    columnStyles: {
      0: { halign: "left", cellWidth: 70 },
      1: { halign: "right", cellWidth: 30 },
    },
    tableWidth: 100,
  });
  y = (doc.lastAutoTable?.finalY || y) + 10;

  // ---- By Program ----
  if (y > 170) {
    doc.addPage();
    y = _drawOverviewHeader(doc, logoData, reportDate);
  }
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("By Program", 14, y);
  y += 2;

  autoTable(doc, {
    ..._overviewTableStyles,
    startY: y,
    head: [["Program", ...OVERVIEW_STAT_KEYS.map(([, label]) => label)]],
    body: programs.map((p) => [
      p.label,
      ...OVERVIEW_STAT_KEYS.map(([key]) => String(p[key] ?? 0)),
    ]),
    columnStyles: {
      0: { halign: "left", cellWidth: 55 },
    },
  });
  y = (doc.lastAutoTable?.finalY || y) + 10;

  // ---- By Cohort ----
  if (y > 160) {
    doc.addPage();
    y = _drawOverviewHeader(doc, logoData, reportDate);
  }
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("By Cohort", 14, y);
  y += 2;

  autoTable(doc, {
    ..._overviewTableStyles,
    startY: y,
    head: [["Cohort", "ID", ...OVERVIEW_STAT_KEYS.map(([, label]) => label)]],
    body: (cohortStats || []).map((c) => [
      c.label || "",
      String(c.cohort_id ?? ""),
      ...OVERVIEW_STAT_KEYS.map(([key]) => String(c[key] ?? 0)),
    ]),
    columnStyles: {
      0: { halign: "left", cellWidth: 50 },
      1: { cellWidth: 12 },
    },
  });

  // Footer on each page
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    const ph = doc.internal.pageSize.getHeight();
    doc.setDrawColor(...HAIR);
    doc.setLineWidth(0.3);
    doc.line(14, ph - 12, pageW - 14, ph - 12);
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.text("Mahindra University · Admissions Registration Portal", 14, ph - 7);
    doc.text(`Page ${i} of ${pageCount}`, pageW - 14, ph - 7, { align: "right" });
  }

  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`MU-Registrations-Summary-${stamp}.pdf`);
};

